import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Table, { TableProps } from '@gym-molecules/Table/Table';
import { useEffect, useState } from 'react';
import Loader from '@gym-atoms/Loader/Loader';
import Button, { ButtonSizes, ButtonVariants } from '@gym-atoms/Button/Button';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import Text from '@gym-atoms/Text/Text';
import { API } from 'aws-amplify';
import { getSiteLocationProgramAccessCategories } from '@gym-graphql/queries';
import { getPartialGymsOfGymChain } from '@gym-particles/queries';
import { partialGymType } from '@gym-particles/types/models';
import { UserRole } from '@gym-particles/types/User';
import {
  GetSiteLocationProgramAccessCategoriesQuery,
  SiteLocationProgramAccessCategory
} from '@gym-src/API';
import {
  GymChainProgramAccessCategoryTable,
  GymChainProgramAccessCategoryType
} from '../../../particles/types/GymChainProgramAccessCategory';
import {
  deleteGymChainProgramAccessCategory,
  fetchGymChainProgramAccessCategory
} from '../../../redux/slices/gymChainProgramAccessCategorySlice';
import { fetchGymChainAccessCategory } from '../../../redux/slices/gymChainAccessCategorySlice';

const hasProgramAccessCategoryDetails = (accessCategory: GymChainProgramAccessCategoryTable) =>
  accessCategory.programAccessCategoryDetails.some(
    (detail) => detail.programAccessCategoryId && detail.programAccessCategoryId > 0
  );

const getProgramAccessCategoryId = (accessCategory: GymChainProgramAccessCategoryType) =>
  accessCategory.accessCategoryId > 0 ? accessCategory.accessCategoryId : accessCategory.id;

const toLocationProgramAccessCategoryDetail = (
  programAccessCategory: SiteLocationProgramAccessCategory
): GymChainProgramAccessCategoryTable['programAccessCategoryDetails'][number] => ({
  accessCategoryId: programAccessCategory.accessCategoryId || null,
  siteLocationId: programAccessCategory.siteLocationId || null,
  locationName: programAccessCategory.locationName || null,
  programAccessCategoryId: programAccessCategory.siteLocationProgramAccessCategoryId || null,
  arxAccessCategoryId: programAccessCategory.arxAccessCategoryId || null,
  programId: programAccessCategory.programId || null,
  programImportedId: programAccessCategory.programImportedId || null,
  programName: programAccessCategory.programName || null,
  scheduleType: programAccessCategory.scheduleType || null
});

const isSameProgramAccessCategoryDetail = (
  current: GymChainProgramAccessCategoryTable['programAccessCategoryDetails'][number],
  next: GymChainProgramAccessCategoryTable['programAccessCategoryDetails'][number]
) =>
  current.programAccessCategoryId === next.programAccessCategoryId &&
  current.programId === next.programId &&
  current.siteLocationId === next.siteLocationId;

const appendUniqueProgramAccessCategoryDetails = (
  currentDetails: GymChainProgramAccessCategoryTable['programAccessCategoryDetails'],
  nextDetails: GymChainProgramAccessCategoryTable['programAccessCategoryDetails']
) =>
  nextDetails.reduce<GymChainProgramAccessCategoryTable['programAccessCategoryDetails']>(
    (details, detail) =>
      details.some((current) => isSameProgramAccessCategoryDetail(current, detail))
        ? details
        : [...details, detail],
    currentDetails
  );

export const BookingAccessCategoryListTable = (
  props: GymChainsBookingAccessCategoryListTableProps
) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [selectedRow, setSelectedRow] = useState<GymChainProgramAccessCategoryTable>();
  const [selectedRows, setSelectedRows] = useState<Array<GymChainProgramAccessCategoryTable>>([]);
  const gymChainId = props.gymChainId;
  const { t } = useTranslation();
  const [gymAccessCategorySortField, setGymAccessCategorySortField] = useState('linkSortOrder');
  const [gymAccessCategorySortOrder, setGymAccessCategorySortOrder] = useState(-1);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const isGymAccessCategoryLoading = useAppSelector(
    (state) => state.gymChainProgramAccessCategories.isAccessCategoriesLoading
  );
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [locationProgramAccessCategories, setLocationProgramAccessCategories] = useState<
    SiteLocationProgramAccessCategory[]
  >([]);

  const gymChainName = useAppSelector(
    (state) => state.gymChain.items.find((g) => g.id === +gymChainId)?.name
  );
  const currentUser = useAppSelector((state) => state.user);
  useEffect(() => {
    dispatch(
      fetchGymChainProgramAccessCategory({
        siteId: props.gymChainId
      })
    );
    dispatch(
      fetchGymChainAccessCategory({
        siteId: props.gymChainId
      })
    );
  }, []);

  useEffect(() => {
    const fetchLocationProgramAccessCategories = async () => {
      const locationsResponse = await (API.graphql({
        query: getPartialGymsOfGymChain,
        variables: {
          id: props.gymChainId,
          offset: 0,
          pageSize: 1000,
          sortField: 'gymName',
          sortOrder: 'ASC',
          userId: currentUser.userRole === UserRole.SYSTEM_ADMIN ? 0 : currentUser.userId
        }
      }) as Promise<{
        data: { getGymsOfGymChain: { items: partialGymType[] } };
      }>);

      const locationProgramResponses = await Promise.all(
        locationsResponse.data.getGymsOfGymChain.items
          .filter((gym) => gym.gymId)
          .map(async (gym) => {
            const response = await (API.graphql({
              query: getSiteLocationProgramAccessCategories,
              variables: {
                siteLocationId: gym.gymId
              }
            }) as Promise<{ data: GetSiteLocationProgramAccessCategoriesQuery }>);

            return response.data.getSiteLocationProgramAccessCategories?.items || [];
          })
      );

      setLocationProgramAccessCategories(
        locationProgramResponses
          .flat()
          .filter(
            (item): item is SiteLocationProgramAccessCategory =>
              Boolean(item) && Boolean(item?.siteLocationProgramAccessCategoryId)
          )
      );
    };

    fetchLocationProgramAccessCategories();
  }, [props.gymChainId, currentUser.userId, currentUser.userRole]);

  const mapAccessCategories = (
    accessCategories?: GymChainProgramAccessCategoryType[],
    membershipLinkedAccessCategoryIds: number[] = [],
    locationProgramAccessCategories: SiteLocationProgramAccessCategory[] = []
  ) => {
    if (!accessCategories) {
      return;
    }
    const membershipLinkedIds = new Set(membershipLinkedAccessCategoryIds);
    const mappedAccessCategories: GymChainProgramAccessCategoryTable[] = [];
    accessCategories
      .filter(
        (accessCategory) =>
          accessCategory.programAccessCategoryId > 0 ||
          !membershipLinkedIds.has(getProgramAccessCategoryId(accessCategory))
      )
      .sort((current, next) => {
        const currentLinked = current.programAccessCategoryId > 0 ? 1 : 0;
        const nextLinked = next.programAccessCategoryId > 0 ? 1 : 0;

        return nextLinked - currentLinked;
      })
      .forEach((accessCategory) => {
        const index = mappedAccessCategories.findIndex((mappedAccessCategory) => {
          return mappedAccessCategory.id === getProgramAccessCategoryId(accessCategory);
        });

        if (index > -1) {
          const locationProgramDetails = locationProgramAccessCategories
            .filter(
              (programAccessCategory) =>
                programAccessCategory.accessCategoryId ===
                getProgramAccessCategoryId(accessCategory)
            )
            .map(toLocationProgramAccessCategoryDetail);

          mappedAccessCategories[index].linkSortOrder = Math.max(
            mappedAccessCategories[index].linkSortOrder || 0,
            accessCategory.programAccessCategoryId > 0 ? 1 : 0
          );
          mappedAccessCategories[
            index
          ].programAccessCategoryDetails = appendUniqueProgramAccessCategoryDetails(
            mappedAccessCategories[index].programAccessCategoryDetails,
            locationProgramDetails.length > 0
              ? locationProgramDetails
              : [
                  {
                    programAccessCategoryId:
                      accessCategory.programAccessCategoryId > 0
                        ? accessCategory.programAccessCategoryId
                        : null,
                    programId: accessCategory.programId > 0 ? accessCategory.programId : null,
                    accessCategoryId:
                      getProgramAccessCategoryId(accessCategory) > 0
                        ? getProgramAccessCategoryId(accessCategory)
                        : null,
                    arxAccessCategoryId:
                      accessCategory.arxAccessCategoryId !== ''
                        ? accessCategory.arxAccessCategoryId
                        : null,
                    programName:
                      accessCategory.programName !== '' ? accessCategory.programName : null,
                    programImportedId:
                      accessCategory.programImportedId > 0
                        ? accessCategory.programImportedId
                        : null,
                    scheduleType:
                      accessCategory.scheduleType !== '' ? accessCategory.scheduleType : null
                  }
                ]
          );
        } else {
          const locationProgramDetails = locationProgramAccessCategories
            .filter(
              (programAccessCategory) =>
                programAccessCategory.accessCategoryId ===
                getProgramAccessCategoryId(accessCategory)
            )
            .map(toLocationProgramAccessCategoryDetail);

          mappedAccessCategories.push({
            id: getProgramAccessCategoryId(accessCategory),
            siteId: accessCategory.siteId,
            name: accessCategory.name,
            gender: accessCategory.gender,
            age: accessCategory.age > 0 ? accessCategory.age : null,
            schedulesOnly: accessCategory.schedulesOnly,
            status: accessCategory.status,
            linkSortOrder: accessCategory.programAccessCategoryId > 0 ? 1 : 0,
            programAccessCategoryDetails:
              locationProgramDetails.length > 0
                ? locationProgramDetails
                : [
                    {
                      programAccessCategoryId:
                        accessCategory.programAccessCategoryId > 0
                          ? accessCategory.programAccessCategoryId
                          : null,
                      programId: accessCategory.programId > 0 ? accessCategory.programId : null,
                      accessCategoryId:
                        getProgramAccessCategoryId(accessCategory) > 0
                          ? getProgramAccessCategoryId(accessCategory)
                          : null,
                      arxAccessCategoryId:
                        accessCategory.arxAccessCategoryId !== ''
                          ? accessCategory.arxAccessCategoryId
                          : null,
                      programName:
                        accessCategory.programName !== '' ? accessCategory.programName : null,
                      programImportedId:
                        accessCategory.programImportedId > 0
                          ? accessCategory.programImportedId
                          : null,
                      scheduleType:
                        accessCategory.scheduleType !== '' ? accessCategory.scheduleType : null
                    }
                  ]
          });
        }
      });

    return mappedAccessCategories;
  };

  const gymChainProgramAccessCategories = useAppSelector((state) =>
    mapAccessCategories(
      state.gymChainProgramAccessCategories.items.find((e) => e.siteId === props.gymChainId)?.items,
      (state.gymChainAccessCategories.items.find((e) => e.siteId === props.gymChainId)?.items || [])
        .filter((accessCategory) => accessCategory.siteLocationMembershipId > 0)
        .map((accessCategory) =>
          accessCategory.accessCategoryId > 0 ? accessCategory.accessCategoryId : accessCategory.id
        ),
      locationProgramAccessCategories
    )
  );

  const deleteDialogHandler = () => {
    selectedRows &&
      dispatch(deleteGymChainProgramAccessCategory(selectedRows.map((row) => row.id)));

    setSelectedRows([]);
    setSelectedRow(undefined);
    setDeleteDialogVisible(false);
  };

  const DeleteDialogFooter = () => {
    return (
      <div>
        <Button
          label={t('COMMON.CANCEL')}
          variant={ButtonVariants.textonly}
          onClick={() => setDeleteDialogVisible(false)}
        />
        <Button
          label={t('COMMON.DELETE')}
          variant={ButtonVariants.danger}
          onClick={deleteDialogHandler}
        />
      </div>
    );
  };

  const tablePropsAccessCategory: TableProps<GymChainProgramAccessCategoryTable> = {
    onChildRowClick: (e) => {
      if (!e['siteLocationId'] || !e['accessCategoryId']) {
        return;
      }

      const programAccessCategory = gymChainProgramAccessCategories?.find(
        (row) => row.id === e['accessCategoryId']
      );

      history.push({
        pathname: `/gymChains/${gymChainId}/gyms/${e['siteLocationId']}/gymChainProgramAccessCategories/${e['accessCategoryId']}`,
        state: {
          from: 'chain',
          gymId: e['siteLocationId'],
          accessCategoryTab: 'bookings',
          programAccessCategory
        }
      });
    },
    selectionMode: 'checkbox',
    enableChildRowClick: true,
    exportFileName: `GymAccessCategories-${gymChainName}`,
    data: gymChainProgramAccessCategories || undefined,
    searchPlaceholderText: t('ACCESS_CATEGORY.SEARCH_FIELD_TEXT'),
    excelBtntext: t('ACCESS_CATEGORY.EXPORT_EXCEL_BUTTON_LABEL'),
    totalRecords: gymChainProgramAccessCategories?.length || 0,
    selectedRows: selectedRows,
    selectedRow: selectedRow,
    pageSize: pageSize,
    offset: offset,
    setPageSize: setPageSize,
    setOffset: setOffset,
    setSelectedRow: setSelectedRow,
    setSelectedRows: setSelectedRows,
    setSortField: setGymAccessCategorySortField,
    setSortOrder: setGymAccessCategorySortOrder,
    sortField: gymAccessCategorySortField,
    sortOrder: gymAccessCategorySortOrder,
    emptyStateTexts: [t('ACCESS_CATEGORY.EMPTY_STATE_TEXT')],
    columns: [
      {
        field: 'name',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_NAME'),
        toggable: false,
        sortable: true,
        truncateText: true
      },
      {
        field: 'gender',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_GENDER'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'age',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_AGE'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'schedulesOnly',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_SCHEDULES_ONLY'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'status',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_STATUS'),
        toggable: true,
        truncateText: true
      }
    ],
    isContextMenu: true,
    menuItem: [
      {
        label: t('COMMON.MODIFY'),
        command: () =>
          history.push(
            `/gymChains/${gymChainId}/gymChainProgramAccessCategories/${selectedRow?.id}`
          )
      },
      {
        label: t('COMMON.DELETE'),
        command: () => {
          setSelectedRows(selectedRow ? [selectedRow] : []);
          setDeleteDialogVisible(true);
        }
      }
    ],
    lazy: false,
    expandable: true,
    isRowExpandable: hasProgramAccessCategoryDetails,
    expandableField: 'programAccessCategoryDetails',
    expandedCols: [
      {
        field: 'locationName',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_LOCATION_NAME'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'programAccessCategoryId',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_PROGRAM_ACCESS_CATEGORY_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'arxAccessCategoryId',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_ARX_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'programId',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_PROGRAM_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'programImportedId',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_PROGRAM_IMPORTED_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'programName',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_PROGRAM_NAME'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'scheduleType',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_SCHEDULE_TYPE'),
        toggable: true,
        truncateText: true
      }
    ],
    headerBtns: [
      <div className="d-flex" key={1}>
        <Button
          label={t('ACCESS_CATEGORY.ADD_NEW_ACCESS_CATEGORY_BUTTON_LABEL')}
          size={ButtonSizes.small}
          onClick={() =>
            history.push(`/gymChains/${gymChainId}/gymChainProgramAccessCategories/new`)
          }
        />
      </div>,
      <div className="d-flex" key={2}>
        <Button
          data-cy={'access-categoryTable -table-btn-inactive-beacons'}
          variant={ButtonVariants.outline}
          label={t('ACCESS_CATEGORY.DELETE_SELECTED_ACCESS_CATEGORY_BUTTON_LABEL')}
          size={ButtonSizes.small}
          onClick={() => {
            setDeleteDialogVisible(true);
          }}
          disabled={selectedRows?.length === 0 || false}
        />
      </div>
    ]
  };
  return (
    <div>
      <DialogBox
        dialogVisible={deleteDialogVisible}
        variant={DialogBoxVariants.long}
        dialogDismissableMask={true}
        onHideCallback={() => setDeleteDialogVisible(false)}
        dialogFooter={<DeleteDialogFooter />}
        dialogClosable={false}
        dialogHeader={t('COMMON.DELETE')}
        content={
          <Text>
            {t('ACCESS_CATEGORY.DELETE_MODAL_CONFIRM')}{' '}
            <span className="fw-bold">{selectedRows?.map((row) => row.name).join(', ')}</span>? ?
          </Text>
        }
      />
      {isGymAccessCategoryLoading ? (
        <Loader shape="table" />
      ) : (
        <Table {...tablePropsAccessCategory} />
      )}
    </div>
  );
};
interface GymChainsBookingAccessCategoryListTableProps {
  gymChainId: number;
}
